/**
 * The FIXED role catalogue (docs/architecture/iam-rbac-uam-implementation.md §2, permission-matrix
 * `roles:`) — ONE shared definition for the IAM console (Users, Permission matrix, invite/edit
 * modals). Eight roles exist; SEVEN are tenant-plane and assignable through UAM. `platform_admin`
 * is the platform plane: the server refuses it here (UAM_ROLE_FORBIDDEN), so it is metadata-only
 * (rendered when seen on a user, never offered as a choice).
 *
 * Custom roles (`x_<slug>`, OPSFL-FSD-IAM-UC2 §5) ride ALONGSIDE this catalogue: they bind existing
 * matrix keys and are assignable via UAM while ACTIVE — render them distinctly, never as catalogue.
 */

export interface RoleMeta {
  role: string;
  label: string;
  blurb: string;
  /** False only for `platform_admin` — visible when held, never offered. */
  assignable: boolean;
}

export const ROLE_CATALOGUE: readonly RoleMeta[] = [
  {
    role: 'hub_admin',
    label: 'Hub admin',
    blurb: 'Full tenant plane, including user management.',
    assignable: true,
  },
  {
    role: 'hub_operator',
    label: 'Hub operator',
    blurb: 'Oversight, spoke provisioning, entitlement & metering reads.',
    assignable: true,
  },
  {
    role: 'flow_designer',
    label: 'Flow designer',
    blurb: 'Author flows, run simulations, work the migration queue & templates.',
    assignable: true,
  },
  {
    role: 'flow_approver',
    label: 'Flow approver',
    blurb: 'The checker leg — approve and publish flows.',
    assignable: true,
  },
  {
    role: 'analyst',
    label: 'Analyst',
    blurb: 'The investigator workbench: triage, RFI, entity-360.',
    assignable: true,
  },
  {
    role: 'auditor',
    label: 'Auditor',
    blurb: 'Read-only across the whole tenant plane (examiner independence is enforced).',
    assignable: true,
  },
  {
    role: 'hub_user',
    label: 'Hub user',
    blurb: 'Base member — no privileged grants.',
    assignable: true,
  },
  {
    role: 'platform_admin',
    label: 'Platform admin',
    blurb: 'Platform plane only — never assignable through user management.',
    assignable: false,
  },
] as const;

/** The seven roles UAM may assign (`ASSIGNABLE_BY_UAM` server-side; gate G18 keeps it static). */
export const ASSIGNABLE_ROLES: readonly RoleMeta[] = ROLE_CATALOGUE.filter((r) => r.assignable);

export const ROLE_LABEL: ReadonlyMap<string, string> = new Map(
  ROLE_CATALOGUE.map((r) => [r.role, r.label]),
);

export const CATALOGUE_ROLE_NAMES: readonly string[] = ROLE_CATALOGUE.map((r) => r.role);

/** Well-formed tenant-authored custom role name (`x_` then lowercase slug — RoleCatalogue.CUSTOM_PREFIX). */
export function isCustomRoleName(role: string): boolean {
  return /^x_[a-z0-9_]+$/.test(role);
}
